import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ClipboardList,
  Plus,
  Search,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  User,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Animal, Task, Enclosure } from '../LivestockManagementPage';

// Import design system tokens
import { 
  getCardClasses, 
  getShadowClasses, 
  getButtonClasses,
  components, 
  colors, 
  typography,
  spacing
} from '@/components/business/design-system/businessDesignTokens';

interface TasksTabProps {
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  animals: Animal[];
  enclosures: Enclosure[];
}

export function TasksTab({ tasks, setTasks, animals, enclosures }: TasksTabProps) {
  const { t } = useTranslation('business', { keyPrefix: 'livestock.tasksTab' });
  const { t: tc } = useTranslation('common');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  
  // Form state for adding/editing tasks
  const [formData, setFormData] = useState<Partial<Task>>({
    title: '',
    description: '',
    animalId: '',
    enclosureId: '',
    assignedTo: '',
    dueDate: new Date().toISOString().split('T')[0],
    priority: 'Medium',
    status: 'To Do',
    category: 'Other'
  });

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = searchTerm === '' || 
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.assignedTo.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  // Task statistics
  const taskStats = {
    total: tasks.length,
    todo: tasks.filter(t => t.status === 'To Do').length,
    inProgress: tasks.filter(t => t.status === 'In Progress').length,
    completed: tasks.filter(t => t.status === 'Completed').length,
    overdue: tasks.filter(t => {
      const dueDate = new Date(t.dueDate);
      const today = new Date();
      return dueDate < today && t.status !== 'Completed';
    }).length
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveTask = () => {
    if (!formData.title || !formData.assignedTo || !formData.dueDate) {
      alert('Please fill in required fields');
      return;
    }

    if (showEditDialog && selectedTask) {
      // Update existing task
      setTasks(prev => 
        prev.map(task => 
          task.id === selectedTask.id 
            ? { ...task, ...formData } as Task
            : task
        )
      );
    } else {
      // Add new task
      const newTask: Task = {
        ...formData,
        id: `t${Date.now()}`
      } as Task;
      
      setTasks(prev => [...prev, newTask]);
    }

    // Reset form and close dialog
    setFormData({
      title: '', description: '', animalId: '', enclosureId: '',
      assignedTo: '', dueDate: new Date().toISOString().split('T')[0],
      priority: 'Medium', status: 'To Do', category: 'Other'
    });
    setShowAddDialog(false);
    setShowEditDialog(false);
    setSelectedTask(null);
  };

  const handleCompleteTask = (taskId: string) => {
    setTasks(prev => 
      prev.map(task => 
        task.id === taskId 
          ? { ...task, status: 'Completed' as const }
          : task
      )
    );
  };

  const handleDeleteTask = (taskId: string) => {
    if (confirm('Are you sure you want to delete this task?')) {
      setTasks(prev => prev.filter(task => task.id !== taskId));
    }
  };

  const openEditDialog = (task: Task) => {
    setSelectedTask(task);
    setFormData({ ...task });
    setShowEditDialog(true);
  };

  const getAnimalName = (animalId: string) => {
    const animal = animals.find(a => a.id === animalId);
    return animal ? animal.name : '';
  };

  const getEnclosureName = (enclosureId: string) => {
    const enclosure = enclosures.find(e => e.id === enclosureId);
    return enclosure ? enclosure.name : '';
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'Urgent': return 'destructive';
      case 'High': return 'default';
      case 'Medium': return 'secondary';
      case 'Low': return 'outline';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'To Do': return 'outline';
      case 'In Progress': return 'secondary';
      case 'Completed': return 'default';
      default: return 'secondary';
    }
  };

  const isOverdue = (task: Task) => {
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    return dueDate < today && task.status !== 'Completed';
  };

  return (
    <div className={spacing.sectionGap}>
      {/* Streamlined Header */}
      <div className="mb-6">
        <p className="text-sm text-slate-600">{t('description')}</p>
      </div>

      {/* Streamlined Stats and Actions */}
      <Card className={getCardClasses('basic')}>
        <CardContent className="p-4">
          {/* Compact Stats Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-900">{taskStats.total}</div>
                  <div className="text-xs text-slate-600">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-600">{taskStats.todo}</div>
                  <div className="text-xs text-slate-600">To Do</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">{taskStats.inProgress}</div>
                  <div className="text-xs text-slate-600">In Progress</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-emerald-600">{taskStats.completed}</div>
                  <div className="text-xs text-slate-600">Completed</div>
                </div>
                {taskStats.overdue > 0 && (
                  <div className="text-center">
                    <div className="text-lg font-bold text-red-600">{taskStats.overdue}</div>
                    <div className="text-xs text-slate-600">Overdue</div>
                  </div>
                )}
              </div>
            </div>
            <Button 
              onClick={() => setShowAddDialog(true)} 
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Task
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Compact Search and Filters */}
      <div className="space-y-4">
        {/* Compact Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search tasks, descriptions, or assignee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="To Do">To Do</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-28">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Health">Health</SelectItem>
                  <SelectItem value="Feeding">Feeding</SelectItem>
                  <SelectItem value="Cleaning">Cleaning</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                  <SelectItem value="Observation">Observation</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Results Count and Clear Filters */}
        <div className="flex items-center justify-between text-sm py-2 border-t border-slate-100">
          <span className="text-slate-600">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </span>
          {(searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all') && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setPriorityFilter('all');
                setCategoryFilter('all');
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Tasks List - Main Content */}
      <Card className={getCardClasses('basic')}>
        <CardContent className="p-6">{filteredTasks.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardList className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No tasks found</h3>
              <p className="text-slate-600 mb-4">
                {tasks.length === 0 
                  ? "Create your first task to get started"
                  : "Try adjusting your search criteria"
                }
              </p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Create First Task
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTasks
                .sort((a, b) => {
                  // Sort by due date, then by priority
                  const dateA = new Date(a.dueDate);
                  const dateB = new Date(b.dueDate);
                  if (dateA.getTime() !== dateB.getTime()) {
                    return dateA.getTime() - dateB.getTime();
                  }
                  const priorityOrder = { 'Urgent': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
                  return priorityOrder[a.priority] - priorityOrder[b.priority];
                })
                .map(task => (
                  <div 
                    key={task.id} 
                    className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                      isOverdue(task) ? 'border-red-200 bg-red-50/30' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-start space-x-3 flex-1">
                        <Checkbox
                          checked={task.status === 'Completed'}
                          onCheckedChange={() => task.status !== 'Completed' && handleCompleteTask(task.id)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className={`font-medium ${task.status === 'Completed' ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                              {task.title}
                            </h3>
                            <Badge variant={getPriorityColor(task.priority)}>
                              {task.priority}
                            </Badge>
                            <Badge variant={getStatusColor(task.status)}>
                              {task.status}
                            </Badge>
                            {isOverdue(task) && (
                              <Badge variant="destructive" className="text-xs">
                                Overdue
                              </Badge>
                            )}
                          </div>
                          
                          <p className={`text-sm mb-2 ${task.status === 'Completed' ? 'text-slate-400' : 'text-slate-600'}`}>
                            {task.description}
                          </p>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-3 h-3" />
                              <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                            </div>
                            
                            <div className="flex items-center space-x-1">
                              <User className="w-3 h-3" />
                              <span>{task.assignedTo}</span>
                            </div>
                            
                            <div className="flex items-center space-x-1">
                              <Filter className="w-3 h-3" />
                              <span>{task.category}</span>
                            </div>
                            
                            {task.animalId && (
                              <div className="flex items-center space-x-1">
                                <span>Animal: {getAnimalName(task.animalId)}</span>
                              </div>
                            )}
                            
                            {task.enclosureId && (
                              <div className="flex items-center space-x-1">
                                <span>Location: {getEnclosureName(task.enclosureId)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(task)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Task
                          </DropdownMenuItem>
                          {task.status !== 'Completed' && (
                            <DropdownMenuItem onClick={() => handleCompleteTask(task.id)}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark Complete
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteTask(task.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Task
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Task Dialog */}
      <Dialog 
        open={showAddDialog || showEditDialog} 
        onOpenChange={(open) => {
          if (!open) {
            setShowAddDialog(false);
            setShowEditDialog(false);
            setSelectedTask(null);
            setFormData({
              title: '', description: '', animalId: '', enclosureId: '',
              assignedTo: '', dueDate: new Date().toISOString().split('T')[0],
              priority: 'Medium', status: 'To Do', category: 'Other'
            });
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {showEditDialog ? `Edit Task: ${selectedTask?.title}` : 'Create New Task'}
            </DialogTitle>
            <DialogDescription>
              {showEditDialog 
                ? 'Update the task information below'
                : 'Fill in the details for the new task'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                value={formData.title || ''}
                onChange={(e) => handleFormChange('title', e.target.value)}
                placeholder="What needs to be done?"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleFormChange('description', e.target.value)}
                placeholder="Detailed description of the task..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="assignedTo">Assigned To *</Label>
                <Input
                  id="assignedTo"
                  value={formData.assignedTo || ''}
                  onChange={(e) => handleFormChange('assignedTo', e.target.value)}
                  placeholder="Staff member name"
                />
              </div>
              
              <div>
                <Label htmlFor="dueDate">Due Date *</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={formData.dueDate || ''}
                  onChange={(e) => handleFormChange('dueDate', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => handleFormChange('priority', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleFormChange('status', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="To Do">To Do</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => handleFormChange('category', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Health">Health</SelectItem>
                    <SelectItem value="Feeding">Feeding</SelectItem>
                    <SelectItem value="Cleaning">Cleaning</SelectItem>
                    <SelectItem value="Maintenance">Maintenance</SelectItem>
                    <SelectItem value="Observation">Observation</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="animalId">Related Animal (Optional)</Label>
                <Select value={formData.animalId || "none"} onValueChange={(value) => handleFormChange('animalId', value === "none" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select animal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific animal</SelectItem>
                    {animals.map(animal => (
                      <SelectItem key={animal.id} value={animal.id}>
                        {animal.name} ({animal.species})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="enclosureId">Related Enclosure (Optional)</Label>
                <Select value={formData.enclosureId || "none"} onValueChange={(value) => handleFormChange('enclosureId', value === "none" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select enclosure" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific enclosure</SelectItem>
                    {enclosures.map(enclosure => (
                      <SelectItem key={enclosure.id} value={enclosure.id}>
                        {enclosure.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setShowEditDialog(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveTask} className="bg-emerald-600 hover:bg-emerald-700">
              {showEditDialog ? 'Update Task' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
